import { Component, DestroyRef, Input, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { ClipboardModule } from 'ngx-clipboard';
import { Tenant } from '@parashift/shared/models';
import { ErrorResponse, Logger, NotificationService, TenantService } from '@parashift/shared/services';
import { ModalService } from '@parashift/shared/components';

@Component({
  selector: 'url-integration',
  templateUrl: './url-integration.component.html',
  imports: [FaIconComponent, ClipboardModule, TooltipModule]
})
export class UrlIntegrationComponent {
  private tenantService = inject(TenantService);
  private modalService = inject(ModalService);
  private notificationService = inject(NotificationService);
  private logger = inject(Logger);
  private destroyRef = inject<DestroyRef>(DestroyRef);

  @Input()
  set tenant(value) {
    this._tenant = value;
    this.setEmbeddedLoginEnabled();
  }
  get tenant() { return this._tenant; }
  private _tenant: Tenant;

  embedded_login_enabled = false;
  embedded_url_secret: string = undefined;

  toggleUrlIntegration(event: Event) {
    // The `checked` binding tracks embedded_login_enabled, so a cancelled disable re-syncs on the next CD.
    (event.target as HTMLInputElement).checked ? this.enableUrlIntegration() : this.disableUrlIntegration();
  }

  enableUrlIntegration() {
    this.tenantService.enableUrlIntegration(Number(this.tenant.id)).subscribe({
      next: (result) => {
        this.embedded_url_secret = result['secret'];
        this.embedded_login_enabled = true;
      },
      error: (error: ErrorResponse) => {
        this.notificationService.formattedErrorResponse(error);
        this.logger.log('url-integration-component', 'ENABLE URL INTEGRATION Error', error);
      }
    });
  }

  disableUrlIntegration() {
    this.modalService.confirmation({
      initialState: {
        title: $localize `:@@components.url_integration.modal_disable_url_integration_title:Disable Url Integration`,
        description: $localize `:@@components.url_integration.modal_disable_url_integration_description:Do you want to disable the url integration feature? Enabling it again generates a new secret.`
      }
    })
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(() => {
      this.tenantService.disableUrlIntegration(Number(this.tenant.id)).subscribe({
        next: () => {
          this.embedded_login_enabled = false;
          this.embedded_url_secret = undefined;
          this.notificationService.success({
            description: $localize `:@@components.url_integration.disable_url_integration_success_description:The url integration has been disabled`,
            title: $localize `:@@components.url_integration.disable_url_integration_success_title:Successfully disabled`,
          });
        },
        error: (error: ErrorResponse) => {
          this.notificationService.formattedErrorResponse(error);
          this.logger.log('url-integration-component', 'DISABLE URL INTEGRATION Error', error);
        }
      });
    });
  }

  private setEmbeddedLoginEnabled() {
    if (this.tenant) {
      this.embedded_login_enabled = this.tenant.embedded_login_enabled;
    }
  }
}
